import React, { useState, useEffect, useCallback } from 'react';
import { 
  Select, 
  Table, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Tag, 
  Space, 
  Divider,
  message,
  Button,
  Modal,
  Radio
} from 'antd';
import {
  ReloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  GlobalOutlined,
  PlayCircleFilled,
  PauseOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css'; // 导入Ant Design样式
import API from '../services/api'; // 导入API实例
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const { Title } = Typography;

const IPPolicyAdjustmentUser = () => {
  const { hasPermission, isLoading: permissionLoading, userAccountType } = usePermissionCheck('canManageIPs');
  
  const [ip, setIp] = useState(undefined); // 存储IP地址
  const [loadingBtn, setLoadingBtn] = useState(''); // '', 'addUDP', 'delUDP', 'addNonCN', 'delNonCN'
  const [assignedIps, setAssignedIps] = useState([]);
  const [ipLoading, setIpLoading] = useState(false);
  
  // 新增规则管理相关状态
  const [rules, setRules] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState({});
  
  // 添加规则模态框状态
  const [addRuleModalVisible, setAddRuleModalVisible] = useState(false);
  const [selectedRuleType, setSelectedRuleType] = useState('');
  
  // 帮助模态框状态
  const [helpModalVisible, setHelpModalVisible] = useState(false);

  // 获取用户分配的IP列表
  const fetchAssignedIps = useCallback(async (search = '') => {
    setIpLoading(true);
    try {
      const response = await API.get('/api/user/assigned-ips', {
        params: { search: search }
      });

      if (response.data.assignedIps) {
        // 确保正确处理IP数据结构，无论是字符串数组还是对象数组
        const ips = response.data.assignedIps.map(item => {
          // 如果是对象，提取ip字段；如果是字符串，直接返回
          if (typeof item === 'object' && item !== null) {
            return item.ip || item;
          }
          return item;
        });
        setAssignedIps(ips);
      } else {
        message.error('获取IP列表失败');
        setAssignedIps([]);
      }
    } catch (error) {
      console.error('获取IP列表失败:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('获取IP列表失败');
      }
      setAssignedIps([]);
    } finally {
      setIpLoading(false);
    }
  }, []);

  // 获取过滤规则列表
  const fetchFilterRules = async (ipAddress) => {
    if (!ipAddress) {
      message.warning('请选择IP地址');
      return;
    }

    setLoading(true);
    try {
      const response = await API.get('/api/user/filter-rules', {
        params: { ip: ipAddress }
      });

      if (response.data.code === 0) {
        setRules(response.data.data.rules || []);
        setStatistics(response.data.data.statistics || {});
      } else {
        message.error(response.data.msg || '获取过滤规则失败');
        setRules([]);
        setStatistics(null);
      }
    } catch (error) {
      console.error('获取过滤规则失败:', error);
      if (error.response?.data?.msg) {
        message.error(error.response.data.msg);
      } else {
        message.error('获取过滤规则失败');
      }
      setRules([]);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时获取IP列表
  useEffect(() => {
    if (hasPermission) {
      fetchAssignedIps();
    }
  }, [hasPermission, fetchAssignedIps]);

  // 权限检查
  if (!hasPermission) {
    return (
      <PermissionDenied 
        isLoading={permissionLoading}
          permissionName="IP策略"
        userAccountType={userAccountType}
      />
    );
  }



  // 刷新规则数据
  const handleRefresh = () => {
    if (ip) {
      fetchFilterRules(ip);
    }
  };

  // 切换过滤规则状态
  const handleStatusChange = async (ruleId, ruleType, checked) => {
    setSwitchLoading(prev => ({ ...prev, [ruleId]: true }));

    try {
      const response = await API.put('/api/user/filter-rule-status', {
        ip: ip,
        type: ruleType,
        status: checked ? 1 : 0
      });

      if (response.data.code === 0) {
        message.success(response.data.msg);
        // 刷新规则列表
        fetchFilterRules(ip);
      } else {
        message.error(response.data.msg || '操作失败');
      }
    } catch (error) {
      console.error('修改规则状态失败:', error);
      if (error.response?.data?.msg) {
        message.error(error.response.data.msg);
      } else {
        message.error('修改规则状态失败');
      }
    } finally {
      setSwitchLoading(prev => ({ ...prev, [ruleId]: false }));
    }
  };

  // 添加封UDP规则的处理函数
  const handleAddUDPBlock = async () => {
    setLoadingBtn('addUDP');
    try {
      const response = await API.post('/api/user/block-udp', { ip });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封锁UDP流量规则添加成功');
        // 刷新规则列表
        fetchFilterRules(ip);
      } else {
        message.error(response.data.msg || '添加规则失败');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有操作权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('添加封锁UDP规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 删除封UDP规则的处理函数
  const handleDeleteUDPBlock = async () => {
    setLoadingBtn('delUDP');
    try {
      const response = await API.post('/api/user/delete-block-udp', { ip });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封锁UDP流量规则删除成功');
        // 刷新规则列表
        fetchFilterRules(ip);
      } else {
        // 适配新的错误消息
        if (response.data.msg?.includes('未找到对应的UDP过滤规则')) {
          message.error('未找到对应的UDP过滤规则，可能已被删除');
        } else {
          message.error(response.data.msg || '删除规则失败');
        }
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        // 适配新的错误消息
        if (err.response.data.msg.includes('未找到对应的UDP过滤规则')) {
          message.error('未找到对应的UDP过滤规则，可能已被删除');
        } else {
          message.error(err.response.data.msg);
        }
      } else if (err.response?.status === 403) {
        message.error('没有操作权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('删除封锁UDP规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 添加封海外规则的处理函数
  const handleAddNonChinaBlock = async () => {
    setLoadingBtn('addNonCN');
    try {
      const response = await API.post('/api/user/apply-rule-block-non-china', { ip });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封海外规则添加成功');
        // 刷新规则列表
        fetchFilterRules(ip);
      } else {
        message.error(response.data.msg || '添加封海外规则失败');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有操作权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('添加封海外规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 删除封海外规则的处理函数
  const handleDeleteNonChinaBlock = async () => {
    setLoadingBtn('delNonCN');
    try {
      const response = await API.post('/api/user/delete-apply-rule-block-non-china', { ip });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封海外规则删除成功');
        // 刷新规则列表
        fetchFilterRules(ip);
      } else {
        // 适配新的错误消息
        if (response.data.msg?.includes('未找到对应的封海外应用规则')) {
          message.error('未找到对应的封海外应用规则，可能已被删除');
        } else {
          message.error(response.data.msg || '删除封海外规则失败');
        }
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        // 适配新的错误消息
        if (err.response.data.msg.includes('未找到对应的封海外应用规则')) {
          message.error('未找到对应的封海外应用规则，可能已被删除');
        } else {
          message.error(err.response.data.msg);
        }
      } else if (err.response?.status === 403) {
        message.error('没有操作权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('删除封海外规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 显示添加规则模态框
  const showAddRuleModal = () => {
    setSelectedRuleType('');
    setAddRuleModalVisible(true);
  };

  // 确认添加规则
  const handleAddRule = async () => {
    if (!selectedRuleType) {
      message.warning('请选择要添加的规则类型');
      return;
    }

    if (selectedRuleType === 'udp') {
      await handleAddUDPBlock();
    } else if (selectedRuleType === 'oversea') {
      await handleAddNonChinaBlock();
    }

    setAddRuleModalVisible(false);
    setSelectedRuleType('');
  };

  // 定义表格列
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '规则名称',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => {
        const typeMap = {
          'udp': { color: 'blue', text: '封UDP' },
          'oversea': { color: 'blue', text: '封海外' },
          'custom': { color: 'orange', text: '自定义' }
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '方向',
      dataIndex: 'direction',
      key: 'direction',
      width: 80,
      render: (direction) => {
        const directionMap = {
          'in': { color: 'blue', text: '接收' },
          'out': { color: 'orange', text: '发送' },
          'both': { color: 'purple', text: '双向' }
        };
        const config = directionMap[direction] || { color: 'default', text: direction };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '行为规则',
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (action) => {
        const actionMap = {
          'deny': { color: 'red', text: '拦截' },
          'allow': { color: 'green', text: '允许通过' },
          'drop': { color: 'volcano', text: '丢弃' }
        };
        const config = actionMap[action] || { color: 'default', text: action };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '过滤类型',
      dataIndex: 'type',
      key: 'type_detail',
      width: 100,
      render: (type) => {
        const typeMap = {
          'udp': { color: 'blue', text: 'UDP流量' },
          'oversea': { color: 'blue', text: '海外流量' },
          'tcp': { color: 'green', text: 'TCP协议' },
          'icmp': { color: 'orange', text: 'ICMP协议' },
          'custom': { color: 'purple', text: '自定义' }
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '已启用' : '已禁用'}
        </Tag>
      ),
    },

          {
        title: '操作',
        key: 'actions',
        width: 250,
        render: (_, record) => (
          <Space>
            <Button 
              type={record.status === 1 ? "default" : "primary"}
              size="small"
              disabled={switchLoading[record.id]} 
              loading={switchLoading[record.id]}
              onClick={() => handleStatusChange(record.id, record.type, record.status === 0)}
              icon={record.status === 1 ? <PauseOutlined /> : <PlayCircleFilled />}
            >
              {record.status === 1 ? '禁用' : '启用'}
            </Button>
            {record.type === 'udp' && (
              <Button 
                type="primary"
                danger
                size="small"
                disabled={loadingBtn !== ''} 
                onClick={handleDeleteUDPBlock}
                loading={loadingBtn === 'delUDP'}
                icon={<MinusCircleOutlined />}
              >
                删除
              </Button>
            )}
            {record.type === 'oversea' && (
              <Button 
                type="primary"
                danger
                size="small"
                disabled={loadingBtn !== ''} 
                onClick={handleDeleteNonChinaBlock}
                loading={loadingBtn === 'delNonCN'}
                icon={<GlobalOutlined />}
              >
                删除
              </Button>
            )}
          </Space>
        ),
      },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          过滤规则管理
        </Title>



        {/* IP选择区域 */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>IP地址：</span>
              <Select
                placeholder="请选择IP地址"
                style={{ width: '300px' }}
                value={ip}
                onChange={(value) => {
                  setIp(value);
                  if (value) {
                    fetchFilterRules(value);
                  } else {
                    setRules([]);
                    setStatistics(null);
                  }
                }}
                loading={ipLoading}
                showSearch
                filterOption={false}
                onSearch={(value) => fetchAssignedIps(value)}
                notFoundContent={ipLoading ? '加载中...' : '暂无可用IP'}
                allowClear
              >
                {assignedIps.map(ipAddr => (
                  <Select.Option key={ipAddr} value={ipAddr}>
                    {ipAddr}
                  </Select.Option>
                ))}
              </Select>
            </div>
                         <Space>
               <Button 
                 type="primary"
                 disabled={loadingBtn !== '' || !ip} 
                 onClick={showAddRuleModal}
                 icon={<PlusCircleOutlined />}
               >
                 添加规则
               </Button>
               <Button 
                 onClick={handleRefresh}
                 disabled={!ip}
                 loading={loading}
                 icon={<ReloadOutlined />}
               >
                 刷新
               </Button>
               <Button 
                 onClick={() => setHelpModalVisible(true)}
                 icon={<QuestionCircleOutlined />}
               >
                 使用帮助
               </Button>
             </Space>
          </div>

          {/* 统计信息 */}
          {statistics && (
            <div>
              <Divider />
              <Row gutter={16}>
                                 <Col span={6}>
                   <Statistic
                     title="总规则数"
                     value={statistics.total || 0}
                   />
                 </Col>
                 <Col span={6}>
                   <Statistic
                     title="封UDP规则"
                     value={statistics.udp || 0}
                     valueStyle={{ color: '#ff4d4f' }}
                   />
                 </Col>
                 <Col span={6}>
                   <Statistic
                     title="封海外规则"
                     value={statistics.oversea || 0}
                     valueStyle={{ color: '#1890ff' }}
                   />
                 </Col>
                 <Col span={6}>
                   <Statistic
                     title="已启用规则"
                     value={statistics.enabled || 0}
                     valueStyle={{ color: '#52c41a' }}
                   />
                 </Col>
              </Row>
            </div>
          )}
        </Card>

        {/* 规则列表 */}
        {ip && (
          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
                过滤规则列表 - {ip}
              </Title>
            </div>

            <Table
              columns={columns}
              dataSource={rules}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        )}

        {/* 添加规则模态框 */}
        <Modal
          title="添加过滤规则"
          open={addRuleModalVisible}
          onCancel={() => {
            setAddRuleModalVisible(false);
            setSelectedRuleType('');
          }}
          onOk={handleAddRule}
          okText="确认添加"
          cancelText="取消"
          confirmLoading={loadingBtn !== ''}
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: '16px' }}>
              <span>选择要添加的规则类型：</span>
            </div>
            <Radio.Group 
              value={selectedRuleType} 
              onChange={(e) => setSelectedRuleType(e.target.value)}
              style={{ width: '100%' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Radio value="udp">
                  <div>
                    <div style={{ fontWeight: 'bold' }}>封UDP规则</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      封禁UDP流量，防止UDP洪水攻击
                    </div>
                  </div>
                </Radio>
                <Radio value="oversea">
                  <div>
                    <div style={{ fontWeight: 'bold' }}>封海外规则</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      封禁来自海外的所有流量
                    </div>
                  </div>
                </Radio>
              </div>
            </Radio.Group>
                     </div>
         </Modal>

         {/* 使用帮助模态框 */}
         <Modal
           title="使用帮助"
           open={helpModalVisible}
           onCancel={() => setHelpModalVisible(false)}
           footer={[
             <Button key="close" onClick={() => setHelpModalVisible(false)}>
               关闭
             </Button>
           ]}
           width={700}
           centered
         >
           <div style={{ padding: '16px 0' }}>
             <Typography.Title level={4}>功能说明</Typography.Title>
             <Typography.Text>
               过滤规则管理用于对指定IP地址添加和管理各种流量过滤规则，包括UDP流量封禁和海外流量封禁等。
             </Typography.Text>
             
             <Divider />
             
             <Typography.Title level={4}>使用步骤</Typography.Title>
             <ol style={{ paddingLeft: '20px' }}>
               <li><strong>选择IP地址：</strong>从下拉列表中选择需要配置规则的IP地址</li>
               <li><strong>查看规则：</strong>选择IP后会自动加载该IP的所有过滤规则</li>
               <li><strong>添加规则：</strong>点击"添加规则"按钮，选择要添加的规则类型</li>
               <li><strong>管理规则：</strong>在规则列表中可以启用/禁用或删除已有规则</li>
             </ol>
             
             <Divider />
             
             <Typography.Title level={4}>规则类型</Typography.Title>
             <ul style={{ paddingLeft: '20px' }}>
               <li><strong>封UDP规则：</strong>封禁UDP流量，有效防止UDP洪水攻击</li>
               <li><strong>封海外规则：</strong>封禁来自海外的所有流量，提高安全性</li>
             </ul>
             
             <Divider />
             
             <Typography.Title level={4}>操作说明</Typography.Title>
             <ul style={{ paddingLeft: '20px' }}>
               <li><strong>启用/禁用：</strong>控制规则的生效状态，禁用的规则不会执行</li>
               <li><strong>删除：</strong>永久删除规则，删除后需要重新添加</li>
               <li><strong>刷新：</strong>重新获取最新的规则列表和统计信息</li>
             </ul>
             
             <Divider />
             
             <Typography.Title level={4}>注意事项</Typography.Title>
             <ul style={{ paddingLeft: '20px' }}>
               <li>添加规则前请确保已选择正确的IP地址</li>
               <li>封海外规则可能会影响正常的海外业务访问，请谨慎使用</li>
               <li>删除规则操作不可撤销，请确认后再执行</li>
               <li>规则生效可能需要几分钟时间，请耐心等待</li>
               <li>如遇到问题，请联系技术支持人员</li>
             </ul>
           </div>
         </Modal>
        
      </div>
    </div>
  );
};

export default IPPolicyAdjustmentUser;
